import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MaterialModule } from '../../material.module';
import { ModalPopupMessages, popupButtons, RolesAndPermissionPrintKeys } from '../../utility/constants';
import {
  NestedPrintOption,
  NestedPrintPreviewData,
  NestedPrintRecord,
  NestedPrintSection,
} from '../../models/nested-print.model';
import {
  isPrintPayloadMessage,
  startPrintPreviewReadyNotifier,
} from '../../utility/print-layout.util';

@Component({
  selector: 'app-nested-print-preview',
  standalone: true,
  imports: [CommonModule, MaterialModule],
  templateUrl: './nested-print-preview.component.html',
  styleUrl: './nested-print-preview.component.scss',
})
export class NestedPrintPreviewComponent implements OnInit, OnDestroy {
  private printPayloadReceived = false;
  private stopReadyNotifier?: () => void;
  public previewData!: NestedPrintPreviewData;
  public popupButtons = popupButtons;
  public warningMessage = ModalPopupMessages.printWarningContent;
  public isPrintClicked = false;

  constructor() {
    window.addEventListener('message', this.messageHandler);
  }

  ngOnInit(): void {
    this.stopReadyNotifier = startPrintPreviewReadyNotifier(
      () => this.printPayloadReceived
    );
  }

  ngOnDestroy(): void {
    this.stopReadyNotifier?.();
    window.removeEventListener('message', this.messageHandler);
  }

  public messageHandler = (event: MessageEvent<NestedPrintPreviewData>) => {
    // Only the nested print payload from the opener; ignore any other message.
    if (!isPrintPayloadMessage(event, ['sections', 'records'])) {
      return;
    }
    this.printPayloadReceived = true;
    this.stopReadyNotifier?.();
    this.previewData = event.data;
    if (this.previewData.isPrintDialogRequired) {
      this.print();
    }
  };

  public print(): void {
    this.isPrintClicked = true;
    window.onafterprint = () => {
      window.close();
    };
    setTimeout(() => window.print(), 150);
  }

  public closePreview(): void {
    window.close();
  }

  public isActionPermissions(section: NestedPrintSection): boolean {
    return section.type === RolesAndPermissionPrintKeys.actions;
  }

  public isSummarySection(section: NestedPrintSection): boolean {
    return section.type === RolesAndPermissionPrintKeys.summary;
  }

  public isFieldLevelSection(section: NestedPrintSection): boolean {
    return section.type === RolesAndPermissionPrintKeys.fieldLevel;
  }

  public isFieldLegendSection(section: NestedPrintSection): boolean {
    return section.type === RolesAndPermissionPrintKeys.fieldLegend;
  }

  public shouldShowFieldLevelGroupTitle(section: NestedPrintSection): boolean {
    return section.label.trim().toLowerCase() !== RolesAndPermissionPrintKeys.fields;
  }

  public isCheckboxChecked(option: NestedPrintOption): boolean {
    return option.checked === true;
  }

  public isSectionChecked(section: NestedPrintSection): boolean {
    return this.getSectionSelectionState(section).checked;
  }

  public isSectionIndeterminate(section: NestedPrintSection): boolean {
    return this.getSectionSelectionState(section).indeterminate;
  }

  public isTabActive(section: NestedPrintSection, activeId?: string): boolean {
    return section.id === activeId;
  }

  public pairOptions(options: NestedPrintOption[] = []): NestedPrintOption[][] {
    const pairs: NestedPrintOption[][] = [];
    for (let index = 0; index < options.length; index += 2) {
      pairs.push(options.slice(index, index + 2));
    }
    return pairs;
  }

  public permissionValue(option: NestedPrintOption): string {
    if (typeof option.value === 'boolean') {
      return option.value ? 'Yes' : 'No';
    }
    return option.value == null ? '' : String(option.value);
  }

  public trackRecord(_: number, record: NestedPrintRecord): string | number {
    return record.id;
  }

  public trackSection(_: number, section: NestedPrintSection): string {
    return section.id;
  }

  private getSectionSelectionState(
    section: NestedPrintSection
  ): { checked: boolean; indeterminate: boolean } {
    const optionValues = (section.columns ?? []).map(option =>
      this.isCheckboxChecked(option)
    );
    const childStates = (section.children ?? []).map(child =>
      this.getSectionSelectionState(child)
    );
    const childValues = childStates.flatMap(state =>
      state.indeterminate ? [true, false] : [state.checked]
    );
    const values = [...optionValues, ...childValues];

    if (!values.length) {
      return { checked: false, indeterminate: false };
    }

    const checkedCount = values.filter(Boolean).length;
    if (checkedCount === values.length) {
      return { checked: true, indeterminate: false };
    }
    if (checkedCount === 0) {
      return { checked: false, indeterminate: false };
    }
    return { checked: false, indeterminate: true };
  }
}
